import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PullToRefresh } from '@/components/PullToRefresh';
import { toast } from 'sonner';
import {
  FileText, FolderOpen, Plane, Kanban, CalendarDays, Activity,
  CheckCircle2, AlertTriangle, XCircle, ArrowRight, Sparkles, Send, Loader2, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [qaAnswer, setQaAnswer] = useState(null);
  const [qaHistory, setQaHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/stats`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    await fetchStats();
    toast.success('Dashboard refreshed');
  }, [fetchStats]);

  const seedData = async () => {
    setSeeding(true);
    try {
      await fetch(`${API}/casa/seed`, { method: 'POST', credentials: 'include' });
      await fetch(`${API}/operations/seed`, { method: 'POST', credentials: 'include' });
      await fetch(`${API}/documents/seed-company`, { method: 'POST', credentials: 'include' });
      await fetch(`${API}/medical/seed`, { method: 'POST', credentials: 'include' });
      await fetchStats();
    } catch (err) {
      console.error('Seed error:', err);
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleAsk = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setQaAnswer(null);
    try {
      const res = await fetch(`${API}/ai/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question: question.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setQaAnswer(data);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to get answer');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setAsking(false);
    }
  };

  const fetchQaHistory = async () => {
    try {
      const res = await fetch(`${API}/ai/qa-history`, { credentials: 'include' });
      if (res.ok) setQaHistory(await res.json());
    } catch {}
  };

  const ops = stats?.operations || {};
  const score = ops.compliance_score || 0;

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="dashboard-page" className="p-4 sm:p-6 md:p-8 space-y-6 md:space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              The Centre of Knowledge
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Welcome back, <span className="text-slate-300">{user?.name}</span>
            </p>
          </div>
        {stats && stats.casa_documents === 0 && (
          <Button
            data-testid="seed-data-btn"
            onClick={seedData}
            disabled={seeding}
            className="bg-[#003087] hover:bg-[#003087]/90 text-white w-full sm:w-auto"
          >
            {seeding ? 'Loading...' : 'Load Sample Data'}
          </Button>
        )}
      </div>

      {/* Question Box */}
      <Card data-testid="question-box" className="bg-slate-900 border-slate-800 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#0ea5e9]" />
            <p className="font-mono text-xs uppercase tracking-wider text-slate-400">Ask the Knowledge Base</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="question-input"
              placeholder="Ask about CASA regulations, procedures..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !asking && handleAsk()}
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 flex-1"
            />
            <Button
              data-testid="ask-btn"
              onClick={handleAsk}
              disabled={asking || !question.trim()}
              className="bg-[#003087] hover:bg-[#003087]/90 text-white px-5 w-full sm:w-auto"
            >
              {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 sm:mr-0" /><span className="sm:hidden ml-2">Ask</span></>}
            </Button>
          </div>

          {asking && (
            <div className="flex items-center gap-2 mt-4 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin text-[#0ea5e9]" />
              Scanning all documents...
            </div>
          )}

          {qaAnswer && (
            <div data-testid="qa-answer" className="mt-4 bg-slate-950/60 border border-slate-800 rounded-md p-4 animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MessageSquare className="h-3 w-3 text-[#0ea5e9]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 mb-2 font-mono">{qaAnswer.question}</p>
                  <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{qaAnswer.answer}</div>
                  <p className="text-[10px] text-slate-600 mt-3 font-mono">
                    {new Date(qaAnswer.asked_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* History toggle */}
          <button
            data-testid="qa-history-toggle"
            onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchQaHistory(); }}
            className="flex items-center gap-1.5 mt-3 text-[11px] text-slate-500 hover:text-slate-400 transition-colors"
          >
            {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Previous questions
          </button>

          {showHistory && qaHistory.length > 0 && (
            <ScrollArea className="max-h-60 mt-2">
              <div className="space-y-2">
                {qaHistory.map(qa => (
                  <button
                    key={qa.qa_id}
                    onClick={() => { setQuestion(qa.question); setQaAnswer(qa); }}
                    className="w-full text-left bg-slate-950/40 border border-slate-800/50 rounded p-3 hover:border-slate-700 transition-colors"
                  >
                    <p className="text-xs text-slate-400 truncate">{qa.question}</p>
                    <p className="text-[10px] text-slate-600 mt-1 line-clamp-1">{qa.answer?.slice(0, 100)}...</p>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-[140px] sm:h-[180px] bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Compliance Score + Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Compliance Score - Large Card */}
            <Card
              data-testid="compliance-score-card"
              className="col-span-2 bg-slate-900 border-slate-800 shadow-sm"
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-mono text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Compliance Score</p>
                    <p className="text-4xl sm:text-5xl font-bold text-slate-100 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {score}<span className="text-xl sm:text-2xl text-slate-500">%</span>
                    </p>
                  </div>
                  <div className="relative h-16 w-16 sm:h-24 sm:w-24">
                    <svg className="h-16 w-16 sm:h-24 sm:w-24 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#1e293b" strokeWidth="8" />
                      <circle
                        cx="50" cy="50" r="40" fill="none"
                        stroke={score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="8"
                        strokeDasharray={`${score * 2.51} 251`}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span className="text-slate-400">Compliant: <span className="text-slate-200 font-medium">{ops.compliant || 0}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-amber-500" />
                    <span className="text-slate-400">Review: <span className="text-slate-200 font-medium">{ops.review_due || 0}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-red-500" />
                    <span className="text-slate-400">Non-Compliant: <span className="text-slate-200 font-medium">{ops.non_compliant || 0}</span></span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card
              className="bg-slate-900 border-slate-800 shadow-sm cursor-pointer hover:border-slate-700 transition-colors active:scale-[0.98]"
              onClick={() => navigate('/casa-library')}
            >
              <CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full min-h-[120px] sm:min-h-[140px]">
                <div className="flex items-center justify-between">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-[#0ea5e9]" />
                  <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 text-slate-600" />
                </div>
                <div className="mt-3 sm:mt-4">
                  <p className="text-2xl sm:text-3xl font-bold text-slate-100">{stats?.casa_documents || 0}</p>
                  <p className="font-mono text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 mt-1">CASA Docs</p>
                </div>
              </CardContent>
            </Card>

            <Card
              className="bg-slate-900 border-slate-800 shadow-sm cursor-pointer hover:border-slate-700 transition-colors active:scale-[0.98]"
              onClick={() => navigate('/company-docs')}
            >
              <CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full min-h-[120px] sm:min-h-[140px]">
                <div className="flex items-center justify-between">
                  <FolderOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0ea5e9]" />
                  <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 text-slate-600" />
                </div>
                <div className="mt-3 sm:mt-4">
                  <p className="text-2xl sm:text-3xl font-bold text-slate-100">{stats?.company_documents || 0}</p>
                  <p className="font-mono text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 mt-1">Company Docs</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Second row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {/* Operations Summary */}
            <Card className="bg-slate-900 border-slate-800 shadow-sm cursor-pointer hover:border-slate-700 transition-colors active:scale-[0.98]" onClick={() => navigate('/operations')}>
              <CardHeader className="border-b border-slate-800 p-3 sm:p-4">
                <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <Plane className="h-4 w-4 text-[#0ea5e9]" />
                  Operations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                {['135', '121', '138'].map(part => (
                  <div key={part} className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-400">Part {part}</span>
                    <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-[10px] sm:text-xs">Active</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Projects */}
            <Card className="bg-slate-900 border-slate-800 shadow-sm cursor-pointer hover:border-slate-700 transition-colors active:scale-[0.98]" onClick={() => navigate('/projects')}>
              <CardHeader className="border-b border-slate-800 p-3 sm:p-4">
                <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <Kanban className="h-4 w-4 text-[#0ea5e9]" />
                  Projects
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4">
                <p className="text-2xl sm:text-3xl font-bold text-slate-100">{stats?.projects || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Active projects</p>
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card className="bg-slate-900 border-slate-800 shadow-sm cursor-pointer hover:border-slate-700 transition-colors active:scale-[0.98] sm:col-span-2 md:col-span-1" onClick={() => navigate('/calendar')}>
              <CardHeader className="border-b border-slate-800 p-3 sm:p-4">
                <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-[#0ea5e9]" />
                  Upcoming Events
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4">
                {stats?.upcoming_events?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.upcoming_events.slice(0, 3).map(evt => (
                      <div key={evt.event_id} className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: evt.color }} />
                        <span className="text-xs text-slate-300 truncate">{evt.title}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">No upcoming events</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent AI Analyses */}
          {stats?.recent_analyses?.length > 0 && (
            <Card className="bg-slate-900 border-slate-800 shadow-sm">
              <CardHeader className="border-b border-slate-800 p-4">
                <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#0ea5e9]" />
                  Recent AI Analyses
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {stats.recent_analyses.map(a => (
                  <div key={a.analysis_id} className="flex items-center justify-between py-2 border-b border-slate-800/50 last:border-0">
                    <div>
                      <p className="text-sm text-slate-300">{a.doc_title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{new Date(a.analyzed_at).toLocaleDateString()}</p>
                    </div>
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Analyzed</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
      </div>
    </PullToRefresh>
  );
}
